import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../utils/api';
import { Card, Btn, Input, PageHeader } from '../components/UI';

const AUDIENCES = [
  { value: 'all',    label: 'All Users',        desc: 'Every active user on the platform',     icon: '👥' },
  { value: 'pro',    label: 'Pro Users Only',   desc: 'Users currently on the Pro plan',        icon: '⭐' },
  { value: 'free',   label: 'Free Users Only',  desc: 'Users on the Free plan',                 icon: '🆓' },
  { value: 'single', label: 'Specific Email',   desc: 'Send to one particular email address',   icon: '✉️' },
];

const TEMPLATES = [
  { label: 'Blank',           subject: '',                         body: '' },
  { label: 'Welcome back',    subject: 'We miss you at MarsResume!', body: '<p>Hi there,</p>\n<p>We noticed you haven\'t logged in for a while — we\'d love to have you back!</p>\n<p>Your resume builder is waiting for you. Log in now and take your career to the next level.</p>\n<p><a href="https://marsresume.com/dashboard" style="display:inline-block;background:#EF1D28;color:white;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:700;margin-top:8px;">Go to Dashboard →</a></p>' },
  { label: 'Upgrade to Pro',  subject: 'Unlock unlimited resumes with MarsResume Pro', body: '<p>Hi there,</p>\n<p>Did you know that MarsResume Pro gives you access to <strong>unlimited resumes</strong>, AI writing assistance, premium templates, and much more?</p>\n<p>Upgrade today and take your job search to the next level.</p>\n<p><a href="https://marsresume.com/pricing" style="display:inline-block;background:#EF1D28;color:white;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:700;margin-top:8px;">View Pro Plans →</a></p>' },
  { label: 'New feature',     subject: 'Something new just landed on MarsResume 🚀', body: '<p>Hi there,</p>\n<p>We\'ve been working hard behind the scenes and we\'re excited to share something new with you!</p>\n<h3 style="color:#111827;">What\'s new:</h3>\n<p>Describe the new feature here...</p>\n<p><a href="https://marsresume.com/dashboard" style="display:inline-block;background:#EF1D28;color:white;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:700;margin-top:8px;">Try it now →</a></p>' },
];

export const EmailPage = () => {
  const [audience,    setAudience]    = useState('all');
  const [customEmail, setCustomEmail] = useState('');
  const [subject,     setSubject]     = useState('');
  const [body,        setBody]        = useState('');
  const [recipCount,  setRecipCount]  = useState(null);
  const [preview,     setPreview]     = useState(false);
  const [sending,     setSending]     = useState(false);
  const [result,      setResult]      = useState(null); // { sent, failed, failedEmails }
  const [error,       setError]       = useState('');
  const [confirm,     setConfirm]     = useState(false);

  const fetchCount = useCallback(async (aud) => {
    if (aud === 'single') { setRecipCount(null); return; }
    try {
      const { count } = await adminApi.getEmailRecipientCount(aud);
      setRecipCount(count);
    } catch { setRecipCount(null); }
  }, []);

  useEffect(() => { fetchCount(audience); }, [audience, fetchCount]);

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject);
    setBody(tpl.body);
    setResult(null);
    setError('');
  };

  const handleSend = async () => {
    setError('');
    if (!subject.trim()) return setError('Subject is required.');
    if (!body.trim())    return setError('Message body is required.');
    if (audience === 'single' && !customEmail.trim()) return setError('Please enter a recipient email address.');
    setConfirm(true);
  };

  const confirmSend = async () => {
    setConfirm(false);
    setSending(true);
    setResult(null);
    setError('');
    try {
      const res = await adminApi.sendCustomEmail({ audience, customEmail, subject, body });
      setResult(res);
    } catch (e) { setError(e.message); }
    finally { setSending(false); }
  };

  const recipLabel = audience === 'single'
    ? (customEmail || '—')
    : recipCount !== null ? `${recipCount} recipient${recipCount !== 1 ? 's' : ''}` : '…';

  return (
    <div>
      <PageHeader
        title="Send Email"
        subtitle="Compose and send branded emails to your users from support@marsresume.com"
      />

      <div style={{ display: 'grid', gridTemplateColumns: preview ? '1fr 1fr' : '1fr', gap: 24, alignItems: 'start' }}>

        {/* ── Compose panel ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Audience */}
          <Card>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#0F172A', marginBottom: 12 }}>1. Choose Recipients</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {AUDIENCES.map(a => (
                <button key={a.value} onClick={() => { setAudience(a.value); setResult(null); }}
                  style={{
                    border: `2px solid ${audience === a.value ? '#EF1D28' : '#E2E8F0'}`,
                    background: audience === a.value ? '#FFF1F2' : 'white',
                    borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                    textAlign: 'left', transition: 'all 0.15s',
                  }}>
                  <div style={{ fontSize: 18, marginBottom: 4 }}>{a.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{a.label}</div>
                  <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>{a.desc}</div>
                </button>
              ))}
            </div>

            {audience === 'single' && (
              <div style={{ marginTop: 12 }}>
                <Input
                  label="Recipient Email Address"
                  value={customEmail}
                  onChange={v => { setCustomEmail(v); setResult(null); }}
                  placeholder="user@example.com"
                />
              </div>
            )}

            {audience !== 'single' && recipCount !== null && (
              <div style={{ marginTop: 12, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: '#15803D', fontWeight: 600 }}>
                ✓ Will be sent to <strong>{recipCount}</strong> user{recipCount !== 1 ? 's' : ''}
              </div>
            )}
          </Card>

          {/* Templates */}
          <Card>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#0F172A', marginBottom: 10 }}>2. Start with a Template (optional)</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {TEMPLATES.map(t => (
                <button key={t.label} onClick={() => applyTemplate(t)}
                  style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
                  {t.label}
                </button>
              ))}
            </div>
          </Card>

          {/* Subject + Body */}
          <Card>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#0F172A', marginBottom: 14 }}>3. Compose Your Email</div>
            <Input
              label="Subject Line *"
              value={subject}
              onChange={v => { setSubject(v); setResult(null); }}
              placeholder="e.g. Important update from MarsResume"
            />
            <div style={{ marginBottom: 6 }}>
              <label style={{ display: 'block', color: '#64748B', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>
                Message Body * <span style={{ fontWeight: 400, color: '#94A3B8' }}>(HTML supported)</span>
              </label>
              <textarea
                value={body}
                onChange={e => { setBody(e.target.value); setResult(null); }}
                placeholder={'<p>Hi there,</p>\n<p>Your message here...</p>'}
                rows={14}
                style={{ width: '100%', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 13px', fontSize: 13, fontFamily: 'monospace', color: '#0F172A', resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.6 }}
              />
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
                Tip: Use <code style={{ background: '#F1F5F9', padding: '1px 5px', borderRadius: 4 }}>&lt;p&gt;</code>, <code style={{ background: '#F1F5F9', padding: '1px 5px', borderRadius: 4 }}>&lt;strong&gt;</code>, <code style={{ background: '#F1F5F9', padding: '1px 5px', borderRadius: 4 }}>&lt;a href="..."&gt;</code>, <code style={{ background: '#F1F5F9', padding: '1px 5px', borderRadius: 4 }}>&lt;ul&gt;&lt;li&gt;</code> — your content is wrapped in the MarsResume branded template automatically.
              </div>
            </div>
          </Card>

          {/* Error */}
          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', padding: '12px 16px', borderRadius: 8, fontSize: 13 }}>
              {error}
            </div>
          )}

          {/* Result */}
          {result && (
            <div style={{ background: result.failed === 0 ? '#F0FDF4' : '#FFFBEB', border: `1px solid ${result.failed === 0 ? '#BBF7D0' : '#FDE68A'}`, borderRadius: 10, padding: '16px 20px' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: result.failed === 0 ? '#15803D' : '#92400E', marginBottom: 6 }}>
                {result.failed === 0 ? '✅ All emails sent successfully!' : '⚠️ Sent with some errors'}
              </div>
              <div style={{ fontSize: 13, color: '#374151' }}>
                <span style={{ color: '#16A34A', fontWeight: 600 }}>✓ {result.sent} sent</span>
                {result.failed > 0 && <span style={{ color: '#DC2626', fontWeight: 600, marginLeft: 16 }}>✗ {result.failed} failed</span>}
              </div>
              {result.failedEmails?.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#64748B' }}>
                  Failed: {result.failedEmails.join(', ')}{result.failed > 20 ? ` and ${result.failed - 20} more…` : ''}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => setPreview(p => !p)}
              style={{ background: preview ? '#EFF6FF' : '#F8FAFC', border: `1px solid ${preview ? '#BFDBFE' : '#E2E8F0'}`, color: preview ? '#1D4ED8' : '#374151', borderRadius: 8, padding: '9px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
              {preview ? '✕ Hide Preview' : '👁 Preview Email'}
            </button>
            <Btn color="blue" onClick={handleSend} disabled={sending} style={{ flex: 1 }}>
              {sending ? `Sending to ${recipLabel}…` : `Send to ${recipLabel}`}
            </Btn>
          </div>
        </div>

        {/* ── Preview panel ── */}
        {preview && (
          <div style={{ position: 'sticky', top: 80 }}>
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ background: '#F1F5F9', padding: '12px 16px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>EMAIL PREVIEW</div>
                <div style={{ marginLeft: 'auto', fontSize: 11, color: '#94A3B8' }}>From: support@marsresume.com</div>
              </div>

              {/* Fake email client header */}
              <div style={{ padding: '14px 16px', borderBottom: '1px solid #F1F5F9', background: 'white' }}>
                <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}><strong style={{ color: '#0F172A' }}>Subject:</strong> {subject || <em style={{ color: '#CBD5E1' }}>No subject yet</em>}</div>
                <div style={{ fontSize: 12, color: '#64748B' }}><strong style={{ color: '#0F172A' }}>To:</strong> {audience === 'single' ? (customEmail || '—') : AUDIENCES.find(a => a.value === audience)?.label}</div>
              </div>

              {/* Rendered email */}
              <div style={{ maxHeight: 600, overflowY: 'auto', background: '#F8FAFC', padding: 16 }}>
                {/* Branded shell */}
                <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', maxWidth: 520, margin: '0 auto' }}>
                  <div style={{ background: 'linear-gradient(135deg,#0F172A,#1E293B)', padding: '24px 32px', textAlign: 'center' }}>
                    <span style={{ fontSize: 22, fontWeight: 900, color: '#EF1D28' }}>MarsResume</span>
                    <p style={{ margin: '4px 0 0', color: '#64748B', fontSize: 11 }}>Resume That Takes You to Mars</p>
                  </div>
                  <div
                    style={{ padding: '32px', fontSize: 14, color: '#374151', lineHeight: 1.75 }}
                    dangerouslySetInnerHTML={{ __html: body || '<p style="color:#CBD5E1;font-style:italic;">Your message will appear here…</p>' }}
                  />
                  <div style={{ background: '#F8FAFC', borderTop: '1px solid #E5E7EB', padding: '16px 32px', textAlign: 'center' }}>
                    <p style={{ margin: 0, fontSize: 11, color: '#9CA3AF' }}>© {new Date().getFullYear()} MarsResume · All rights reserved</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* ── Confirm modal ── */}
      {confirm && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000 }} onClick={() => setConfirm(false)} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: 'white', borderRadius: 16, padding: '36px 32px', width: 420, zIndex: 1001, boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 16 }}>📨</div>
            <h3 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 800, color: '#0F172A', textAlign: 'center' }}>Ready to send?</h3>
            <p style={{ margin: '0 0 8px', fontSize: 14, color: '#64748B', textAlign: 'center', lineHeight: 1.6 }}>
              You're about to send <strong style={{ color: '#0F172A' }}>"{subject}"</strong>
            </p>
            <p style={{ margin: '0 0 28px', fontSize: 14, color: '#64748B', textAlign: 'center' }}>
              to <strong style={{ color: '#0F172A' }}>{recipLabel}</strong> from <strong style={{ color: '#0F172A' }}>support@marsresume.com</strong>.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setConfirm(false)} style={{ flex: 1, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '11px', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: '#374151', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button onClick={confirmSend} style={{ flex: 1, background: '#EF1D28', border: 'none', borderRadius: 8, padding: '11px', fontWeight: 700, fontSize: 13, cursor: 'pointer', color: 'white', fontFamily: 'inherit' }}>
                Yes, Send Now
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
